import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import {
  communityWorkflowDefaultAccessLevel,
  orgAdminDefaultWorkflowAccessLevel,
  WorkflowAccessLevel,
} from '@aline/common';
import type { ApolloCache, ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FieldFunctionOptions } from '@apollo/client/cache';
import { uniqWith } from 'ramda';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { workflowTypename } from 'src/helpers';
import type { UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';

export function useGetWorkflowPermissions(
  workflowId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowPermissionsForWorkflowQuery>[0]>,
    'variables'
  >
) {
  const { data, error, loading, refetch } = Apollo.useGetWorkflowPermissionsForWorkflowQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
    ...options,
  });

  return {
    error,
    loading,
    permissions: data?.workflow?.permissions ?? [],
    refetch,
    workflow: data?.workflow ?? null,
  };
}

export function useGetWorkflowPermissionsWithSteps(
  workflowId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowPermissionsWithStepsQuery>[0]>,
    'variables'
  >
) {
  const { data, loading, refetch } = Apollo.useGetWorkflowPermissionsWithStepsQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
    ...options,
  });

  return {
    loading,
    permissions: data?.workflow?.permissions ?? [],
    refetch,
    workflow: data?.workflow ?? null,
  };
}

export const workflowAccessLevelsAsc: WorkflowAccessLevel[] = [
  WorkflowAccessLevel.NoAccess,
  WorkflowAccessLevel.Viewer,
  WorkflowAccessLevel.Assignee,
  WorkflowAccessLevel.Owner,
];

/**
 * Hook that determines whether the current user is authorized to edit a Workflow.
 */
export function useCheckWorkflowAuthorization(workflowId?: UUID | null): {
  canEditWorkflow: boolean;
  canViewWorkflow: boolean;
  loading: boolean;
  permissions: ReturnType<typeof useGetWorkflowPermissions>['permissions'];
  workflow: ReturnType<typeof useGetWorkflowPermissions>['workflow'];
  workflowAccessLevel: WorkflowAccessLevel | null;
} {
  const { checkWorkflowAuthorization } = useGetCheckWorkflowAuthorization();
  const { currentUser, loading: loadingCurrentUser } = useCurrentUserQuery();
  const {
    loading: loadingWorkflow,
    permissions,
    workflow,
  } = useGetWorkflowPermissions(workflowId ?? null);
  const { canEditWorkflow, canViewWorkflow, workflowAccessLevel } = checkWorkflowAuthorization(
    workflow,
    currentUser
  );
  const loading = loadingWorkflow || loadingCurrentUser;

  return {
    canEditWorkflow,
    canViewWorkflow,
    loading,
    permissions,
    workflow,
    workflowAccessLevel,
  };
}

interface WorkflowWithAuthorization extends Pick<Apollo.Workflow, 'id' | '__typename'> {
  owner: UserWithOrg;
  permissions: WorkflowPermissionSubset[];
  isCommunityWorkflow?: boolean;
}

/**
 * Hook that determines whether the current user is authorized to edit a Workflow.
 */
export function useGetCheckWorkflowAuthorization() {
  const safeCheckWorkflowAuthorization = useCallback(
    (workflow: WorkflowWithAuthorization | null, user: UserWithOrg | null) => {
      if (!workflow)
        return { canEditWorkflow: false, canViewWorkflow: false, workflowAccessLevel: null };
      if (workflow.owner.id === user?.id) {
        return {
          canEditWorkflow: true,
          canViewWorkflow: true,
          workflowAccessLevel: WorkflowAccessLevel.Owner,
        };
      }

      const userAccessLevels = workflow.permissions.flatMap(p => {
        if (p.timeliness === Apollo.WorkflowPermissionTimeliness.Active && p.account) {
          const isUserPermission = user?.id === p.account.id;
          const isOrgPermission = user?.org?.id === p.account.id;
          if (isUserPermission || isOrgPermission) return [p.accessLevel];
        }
        return [];
      });

      const isWFOrgAdmin =
        user?.role === Apollo.OrgRole.Admin && user?.org?.id === workflow.owner.org?.id;
      if (isWFOrgAdmin) userAccessLevels.push(orgAdminDefaultWorkflowAccessLevel);
      if (workflow.isCommunityWorkflow) userAccessLevels.push(communityWorkflowDefaultAccessLevel);

      const [mostPermissiveAccessLevel = null] = [...userAccessLevels].sort((a, b) => {
        const indexA = workflowAccessLevelsAsc.indexOf(a);
        const indexB = workflowAccessLevelsAsc.indexOf(b);
        return indexB - indexA;
      });
      const canEditWorkflow = mostPermissiveAccessLevel === Apollo.WorkflowAccessLevel.Assignee;
      const canViewWorkflow =
        canEditWorkflow || mostPermissiveAccessLevel === Apollo.WorkflowAccessLevel.Viewer;

      return {
        canEditWorkflow,
        canViewWorkflow,
        workflowAccessLevel: mostPermissiveAccessLevel,
      };
    },
    []
  );

  return {
    checkWorkflowAuthorization: safeCheckWorkflowAuthorization,
  };
}

/**
 * Add workflow permissions to a workflow.
 */
export function useAddWorkflowPermissions(): {
  addWorkflowPermissions: (
    workflowId: UUID,
    options: Parameters<Apollo.AddWorkflowPermissionsMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.AddWorkflowPermissionsMutation>>;
  loading: boolean;
} {
  const [addWorkflowPermissions, { loading }] = Apollo.useAddWorkflowPermissionsMutation();

  return {
    addWorkflowPermissions: (workflowId, options) => {
      return addWorkflowPermissions({
        ...options,

        update: (cache, { data }) => {
          const newPermissionRefs = addPermissionsToCachedWorkflow(cache, {
            permissions: data?.addWorkflowPermissions ?? [],
            workflowId,
          });

          // Have to modify the list of permissions stored top-level in the cache as well as the list of permissions on the workflow object itself.
          cache.modify({
            fields: {
              activePermissions: (permissionRefs = [], { readField }) => {
                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...permissionRefs, ...newPermissionRefs]
                );
              },
            },
          });
        },
      });
    },
    loading,
  };
}

/**
 * Get or create a template's launcher placeholder and add it to the workflow's cached permissions,
 * so pickers and the collaborators panel show it without refetching.
 */
export function useGetOrCreateWorkflowLauncherPermission() {
  const [getOrCreate, { loading }] = Apollo.useGetOrCreateWorkflowLauncherPermissionMutation();

  const getOrCreateWorkflowLauncherPermission = useCallback(
    async (workflowId: UUID) => {
      const { data } = await getOrCreate({
        update: (cache, { data }) => {
          const permission = data?.getOrCreateWorkflowLauncherPermission;
          if (permission)
            addPermissionsToCachedWorkflow(cache, { permissions: [permission], workflowId });
        },
        variables: { workflowId },
      });

      return data?.getOrCreateWorkflowLauncherPermission ?? null;
    },
    [getOrCreate]
  );

  return { getOrCreateWorkflowLauncherPermission, loading };
}

function handleRemovedPermissions(
  permissionRefs: Reference[],
  options: { readField: FieldFunctionOptions['readField']; removedIds: UUID[] }
): Reference[] {
  const { readField, removedIds } = options;
  const removedIdSet = new Set(removedIds);

  return permissionRefs.filter(ref => {
    const id = readField<UUID>('id', ref);
    return id && !removedIdSet.has(id);
  });
}

/**
 * Remove workflow permissions from a workflow.
 */
export function useRemoveWorkflowPermissions(): {
  loading: boolean;
  removeWorkflowPermissions: (
    workflowId: UUID,
    options: Parameters<Apollo.RemoveWorkflowPermissionsMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.RemoveWorkflowPermissionsMutation>>;
} {
  const [removeWorkflowPermissions, { loading }] = Apollo.useRemoveWorkflowPermissionsMutation();

  return {
    loading,
    removeWorkflowPermissions: (workflowId, options) => {
      return removeWorkflowPermissions({
        ...options,
        update: (cache, { data }) => {
          const workflowPermissionsKey: keyof Apollo.Workflow = 'permissions';
          cache.modify({
            fields: {
              [workflowPermissionsKey]: (permissionRefs = [], { readField }) => {
                if (!data?.removeWorkflowPermissions) return permissionRefs;

                return handleRemovedPermissions(permissionRefs, {
                  readField,
                  removedIds: data.removeWorkflowPermissions,
                });
              },
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });

          // Have to modify the list of permissions stored top-level in the cache as well as the list of permissions on the workflow object itself.
          const activePermissionsKey: keyof Apollo.Query = 'activePermissions';
          const queuedPermissionsKey: keyof Apollo.Query = 'queuedPermissions';
          cache.modify({
            fields: {
              [activePermissionsKey]: (permissionRefs = [], { readField }) => {
                if (!data?.removeWorkflowPermissions) return permissionRefs;

                return handleRemovedPermissions(permissionRefs, {
                  readField,
                  removedIds: data.removeWorkflowPermissions,
                });
              },
              [queuedPermissionsKey]: (permissionRefs = [], { readField }) => {
                if (!data?.removeWorkflowPermissions) return permissionRefs;

                return handleRemovedPermissions(permissionRefs, {
                  readField,
                  removedIds: data.removeWorkflowPermissions,
                });
              },
            },
          });
        },
      });
    },
  };
}

/** Appends permissions to the cached workflow's `permissions` list, skipping ones already in it. */
function addPermissionsToCachedWorkflow(
  cache: ApolloCache,
  options: { permissions: Pick<Apollo.WorkflowPermission, '__typename' | 'id'>[]; workflowId: UUID }
): Reference[] {
  const { permissions, workflowId } = options;
  const newPermissionRefs = permissions.flatMap(permission => {
    const newRef = cache.writeFragment({
      data: permission,
      fragment: gql`
        fragment NewWorkflowPermission on WorkflowPermission {
          __typename
          id
        }
      `,
    });

    return newRef ? [newRef] : [];
  });

  cache.modify({
    fields: {
      permissions: (permissionRefs = [], { readField }) => {
        return uniqWith(
          (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
          [...permissionRefs, ...newPermissionRefs]
        );
      },
    },
    id: cache.identify({ __typename: workflowTypename, id: workflowId }),
  });

  return newPermissionRefs;
}
